/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-30 16:41:43
 * @ Modified by: David Helmick
 * @ Modified time: 2025-09-02 15:57:15
 * @ Description: Bar allowing changing views and opening/closing other features on all pages listing properties
 */

import React, { useState } from 'react'

// Centralized lodash imports - much cleaner!
import {
  filter,
  get,
  includes,
  intersection,
  map,
} from '@/utils/lodash'

import MyPropertiesFilter from '@/layout-components/HeaderDrawer'

import {
  Grid,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  Box,
} from '@mui/material'

// import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList'
import GridOnTwoToneIcon from '@mui/icons-material/GridOnTwoTone'
import ListIcon from '@mui/icons-material/List'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import PollTwoToneIcon from '@mui/icons-material/PollTwoTone'
import RoomTwoToneIcon from '@mui/icons-material/RoomTwoTone'

import PortfolioAnalytics from '../../Portfolio/Analytics'
import Map from '../Map'
import withSession from '@/housefolios-components/Session/withSession'
import SelectAllPropertiesToggleCheckbox from './SelectAllPropertiesCheckbox'
import { searchToolbarIconButtonProps, searchToolbarButtonGroupSx } from '@/theme/skinSx'

let sortingChoices = [
  {
    title: 'Price - low to high',
    textAlign: 'left',
    name: 'price-acquisition.analysis.resellPrice-acquisition.analysis.listPrice-asc',
  },
  {
    title: 'Price - high to low',
    textAlign: 'left',
    name: 'price-acquisition.analysis.resellPrice-acquisition.analysis.listPrice-desc',
  },
  {
    title: 'SqFt - low to high',
    name: 'sqft-sqft-sqft-asc',
  },
  {
    title: 'SqFt - high to low',
    name: 'sqft-sqft-sqft-desc',
  },
  {
    title: 'Year - low to high',
    name: 'yearBuilt-year-year-asc',
  },
  {
    title: 'Year - high to low',
    name: 'yearBuilt-year-year-desc',
  },
]

function PropertiesNavComponent(props) {
  const {
    activeTab,
    setActiveTab,
    properties = [],
    filteredProperties = [],
    currentPageProperties = [],
    selected = [],
    selectAll,
    getSearchResults,
    search,
    showMenu,
    handleMenu,
  } = props
  const [searchStatus3, setSearchStatus3] = useState(false)
  const toggleSearch3 = () => setSearchStatus3(!searchStatus3)

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      localStorage.setItem('view', tab)
    }
  }

  const [showMap, setShowMap] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)

  const compare = intersection(
    map(filteredProperties, (property) => property._id),
    selected,
  )

  const [filterReset, setFilterReset] = useState(null)
  const [isSavedFilter, setIsSavedFilter] = useState(false)

  const hiddenUp = useMediaQuery((theme) =>
    theme.breakpoints.up('md'),
  )

  return (
    <>
      <Collapse in={Boolean(showMenu)} sx={{ width: '100%', overflow: 'visible' }}>
        <Grid size={12} sx={{ width: '100%', maxWidth: '100%' }}>
          <Card variant="outlined"
            sx={{
            display: 'block',
            textAlign: 'center',
            '@media (min-width: 576px)': { display: 'flex' },
            '@media (min-width: 768px)': { textAlign: 'left' },
            p: '1.5rem',
            alignItems: 'center',
            backgroundColor: '#f8f9ff',
            justifyContent: 'space-between',
            flexDirection: 'row',
            width: '100%',
            maxWidth: '100%',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            }}
            >
            {hiddenUp ? null : (
              <div
                style={{ position: 'absolute', top: 5, right: 10 }}
              >
                <Typography
                  variant="subtitle2"
                  onClick={() => handleMenu()}
                >
                  Hide Menu
                </Typography>
              </div>
            )}

            <Grid
              container
              spacing={1}
              wrap="nowrap"
              sx={{
                display: 'block',
                '@media (min-width: 576px)': { display: 'flex' },
                alignItems: 'center',
                width: '100%',
                maxWidth: '100%',
                flex: 1,
                minWidth: 0,
              }}
            >
              {(!props.shared || props.sharedProperties) && (
                <SelectAllPropertiesToggleCheckbox
                  currentpage={currentPageProperties.length}
                  allPages={(() => {
                  const useFiltered = props.search || props.filtered
                  const allPagesVal = useFiltered
                  ? filteredProperties.length
                  : properties.length
                  return allPagesVal
                  })()}
                  selected={props.selected}
                  allIds={
                  props.allInvestorIds ||
                  properties.map((p) => p._id)
                  }
                  pageIds={
                  props.currentPageInvestorIds ||
                  currentPageProperties.map((p) => p._id)
                  }
                  selectAll={props.selectAll}
                  deselectAll={props.deselectAll}
                  selectPage={props.selectPage}
                  deselectPage={props.deselectPage}
                  >
                  {(alignment, SelectLabel) => {
                    // Determine checked/indeterminate state
                    const allIds =
                      props.allInvestorIds ||
                      properties.map((p) => p._id)
                    const pageIds =
                      props.currentPageInvestorIds ||
                      currentPageProperties.map((p) => p._id)
                    const selected = props.selected || []
                    let checked = false
                    let indeterminate = false
                    if (alignment === 'all') {
                      checked =
                        selected.length === allIds.length &&
                        allIds.length > 0
                      indeterminate =
                        selected.length > 0 &&
                        selected.length < allIds.length
                    } else {
                      checked =
                        pageIds.length > 0 &&
                        pageIds.every((id) => selected.includes(id))
                      indeterminate =
                        pageIds.some((id) => selected.includes(id)) &&
                        !pageIds.every((id) => selected.includes(id))
                    }
                    return (
                      <>
                        <FormControlLabel
                          label=""
                          control={
                            <Checkbox
                              checked={checked}
                              indeterminate={indeterminate}
                              onChange={() => {
                                if (alignment === 'all') {
                                  if (checked || indeterminate) {
                                    props.deselectAll?.()
                                  } else {
                                    props.selectAll?.()
                                  }
                                } else {
                                  if (checked || indeterminate) {
                                    props.deselectPage?.()
                                  } else {
                                    props.selectPage?.()
                                  }
                                }
                              }}
                            />
                          }
                          style={{ marginRight: 0 }}
                        />
                        <SelectLabel />
                      </>
                    )
                  }}
                </SelectAllPropertiesToggleCheckbox>
              )}
              {!props.shared && (
                <Grid
                  size="auto"
                  sx={[{
                    "@media (min-width: 768px)": { display: "block" },
                  }, { mx: '1rem', display: 'none' }]}
                  >
                  <Box sx={[{ backgroundColor: '#4f5062', position: 'relative', }, {
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    minHeight: 26,
                    right: 0,
                    width: "1px",
                    background: "#eeeff8",
                    overflow: "hidden",
                    opacity: "0.3 !important",
                  }]} />
                  <Box sx={[{ backgroundColor: '#4f5062', position: 'relative', }, {
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    minHeight: 26,
                    right: 0,
                    width: "1px",
                    background: "#eeeff8",
                    overflow: "hidden",
                    opacity: "0.3 !important",
                  }]} />
                </Grid>
              )}
              {!props.propertySearch && (
                <Grid
                  size="auto"
                  sx={[{
                    "@media (min-width: 768px)": { display: "block" },
                  }, {
                    "@media (min-width: 768px)": { py: 0 },
                  }, {
                  display: 'flex',
                  justifyContent: 'center',
                  py: '1rem',
                  }]}
                  >
                  <Box
                    sx={[
                      {
                        position: "relative",
                        transition: "all 0.2s ease-in-out",
                        "& .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
                        "& .MuiTextField-root": {
                          borderWidth: 2,
                          transition: "all 0.2s ease-in-out",
                          background: "#f8f9ff",
                          width: 280,
                          maxWidth: "100%",
                        },
                        "&:hover .MuiTextField-root": { background: "#f4f5fd", width: 210, maxWidth: "100%" },
                        "&.is-active .MuiTextField-root": { background: "#fff", borderColor: "#368cff" },
                      },
                      searchStatus3 && {
                        "& .MuiTextField-root": { background: "#fff", borderColor: "#368cff" },
                      },
                    ]}
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      sx={{ backgroundColor: "#fff", "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important" } }}
                      value={props.search}
                      onChange={(event) => {
                        props.getSearchResults(event.target.value)
                      }}
                      placeholder={
                        props.propertySearch
                          ? 'Search these properties'
                          : props.marketplace
                            ? 'Search this marketplace'
                            : 'Search this portfolio...'
                      }
                      id="input-with-icon-textfield22-3"
                      label={props.propertySearch && props.searchName}
                      onFocus={toggleSearch3}
                      onBlur={toggleSearch3}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon
                                style={{ color: '#bbbbbb' }}
                              />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid size="grow" />
              {/* {props.propertySearch && (
              <Grid style={{ marginRight: 10 }}>
                <Grid container direction='row' spacing={2}>
                  <Grid>
                    <Typography>City:</Typography>
                  </Grid>
                  <Grid>
                    <Typography>{props.properties[0].city}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )} */}
              {activeTab === '1' &&
                !props.lot &&
                !props.commercial &&
                !props.investor && (
                  <Grid size="auto" data-hf-grid="keep">
                    <Select
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={props.ordering}
                      onChange={(event) =>
                        props.setOrdering(event.target.value)
                      }
                      displayEmpty
                    >
                      {map(sortingChoices, (choice) => (
                        <MenuItem
                          key={choice.name}
                          value={choice.name}
                        >
                          {choice.title}
                        </MenuItem>
                      ))}
                      <MenuItem key={'unselected'} value={''}>
                        Added - new to old
                      </MenuItem>
                    </Select>
                  </Grid>
                )}
              <Grid size="auto" data-hf-grid="keep">
                <Grid
                  container
                  spacing={1}
                  wrap="nowrap"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {filterReset && (
                    <Grid size="auto">
                      <Chip
                        color={
                          isSavedFilter ? 'primary' : 'secondary'
                        }
                        variant="outlined"
                        label="Filters Applied"
                        onDelete={filterReset}
                      />
                    </Grid>
                  )}
                  {!props.lot &&
                    !props.commercial &&
                    !props.investor && (
                      <Grid size="auto" data-hf-grid="keep">
                        <ButtonGroup
                          variant="contained"
                          color="primary"
                          sx={searchToolbarButtonGroupSx}
                        >
                          <Tooltip title="Card View">
                            <Button
                              onClick={() => {
                                toggle('1')
                              }}
                              {...searchToolbarIconButtonProps(activeTab === '1')}
                            >
                              <Box component="span" sx={[{
                                height: "20px !important",
                                width: "20px !important",
                                lineHeight: "20px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0,
                              }, { display: "inline-flex" }]}>
                                <GridOnTwoToneIcon />
                              </Box>
                            </Button>
                          </Tooltip>
                          <Tooltip title="List View">
                            <Button
                              onClick={() => {
                                toggle('2')
                              }}
                              {...searchToolbarIconButtonProps(activeTab === '2')}
                            >
                              <Box component="span" sx={[{
                                height: "20px !important",
                                width: "20px !important",
                                lineHeight: "20px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0,
                              }, { display: "inline-flex" }]}>
                                <ListIcon />
                              </Box>
                            </Button>
                          </Tooltip>
                          {!props.public && (
                            <Tooltip title="Kanban View">
                              <Button
                                onClick={() => {
                                  toggle('3')
                                }}
                                {...searchToolbarIconButtonProps(activeTab === '3')}
                              >
                                <Box component="span" sx={[{
                                  height: "20px !important",
                                  width: "20px !important",
                                  lineHeight: "20px !important",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 0,
                                }, { display: "inline-flex" }]}>
                                  <ViewKanbanIcon />
                                </Box>
                              </Button>
                            </Tooltip>
                          )}
                        </ButtonGroup>
                      </Grid>
                    )}
                  {!props.lot &&
                    !props.commercial &&
                    !props.investor &&
                    !props.propertySearch && (
                      <Grid size="auto" data-hf-grid="keep">
                        <ButtonGroup
                          variant="contained"
                          color="primary"
                          sx={searchToolbarButtonGroupSx}
                        >
                          <Tooltip title="Show Analytics">
                            <Button
                              onClick={() =>
                                setShowAnalytics(!showAnalytics)
                              }
                              {...searchToolbarIconButtonProps(showAnalytics)}
                            >
                              <Box component="span" sx={[{
                                height: "20px !important",
                                width: "20px !important",
                                lineHeight: "20px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0,
                              }, { display: "inline-flex" }]}>
                                <PollTwoToneIcon />
                              </Box>
                            </Button>
                          </Tooltip>
                          <Tooltip title="Show Map">
                            <Button
                              onClick={() => setShowMap(!showMap)}
                              {...searchToolbarIconButtonProps(showMap)}
                            >
                              <Box component="span" sx={[{
                                height: "20px !important",
                                width: "20px !important",
                                lineHeight: "20px !important",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0,
                              }, { display: "inline-flex" }]}>
                                <RoomTwoToneIcon />
                              </Box>
                            </Button>
                          </Tooltip>
                          {!props.investor && (
                            <MyPropertiesFilter
                              openFilterDrawer={true}
                              propertySearch={props.propertySearch}
                              lot={props.lot}
                              properties={properties}
                              getFilteredProperties={
                                props.getFilteredProperties
                              }
                              setFilterReset={setFilterReset}
                              setIsSavedFilter={setIsSavedFilter}
                              search={search}
                              getSearchResults={getSearchResults}
                              public={props.public}
                              activeOrg={
                                props.session.me
                                  ? props.session.me.activeOrg
                                  : {}
                              }
                            />
                          )}
                        </ButtonGroup>
                      </Grid>
                    )}
                  {props.lot && (
                    <Grid size="auto" data-hf-grid="keep">
                      <ButtonGroup
                        variant="contained"
                        color="primary"
                        sx={searchToolbarButtonGroupSx}
                      >
                        <MyPropertiesFilter
                          openFilterDrawer={true}
                          propertySearch={props.propertySearch}
                          lot={props.lot}
                          properties={properties}
                          getFilteredProperties={
                            props.getFilteredProperties
                          }
                          setFilterReset={setFilterReset}
                          setIsSavedFilter={setIsSavedFilter}
                          search={search}
                          getSearchResults={getSearchResults}
                          public={props.public}
                          activeOrg={
                            props.session.me
                              ? props.session.me.activeOrg
                              : {}
                          }
                        />
                      </ButtonGroup>
                    </Grid>
                  )}

                  {props.investor && props.commercial && <></>}
                  {props.propertySearch && (
                    <>
                      <Grid size="auto">
                        <ButtonGroup
                          variant="contained"
                          color="primary"
                          sx={searchToolbarButtonGroupSx}
                        >
                          <MyPropertiesFilter
                            openFilterDrawer={true}
                            propertySearch={props.propertySearch}
                            lot={props.lot}
                            properties={properties}
                            getFilteredProperties={
                              props.getFilteredProperties
                            }
                            setFilterReset={setFilterReset}
                            setIsSavedFilter={setIsSavedFilter}
                            search={search}
                            getSearchResults={getSearchResults}
                            public={props.public}
                            activeOrg={
                              props.session.me
                                ? props.session.me.activeOrg
                                : {}
                            }
                          />
                        </ButtonGroup>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {showMap && (
          <Grid size={12}>
            <Collapse in={showMap}>
              <Map
                properties={filteredProperties}
                selected={props.selected}
                selectProperty={props.selectProperty}
                portfolios={props.portfolios}
                marketplace={props.marketplace}
                shared={props.shared}
                public={props.public}
              />
            </Collapse>
          </Grid>
        )}
        {!props.propertySearch && (
          <Grid size={12} sx={{ mt: 3 }}>
            <Collapse
              in={showAnalytics}
              unmountOnExit
              sx={{ overflow: 'visible' }}
            >
              <PortfolioAnalytics
                properties={filteredProperties}
                marketplace={props.marketplace}
                onClose={() => setShowAnalytics(false)}
              />
            </Collapse>
          </Grid>
        )}
      </Collapse>
    </>
  )
}

export default withSession(PropertiesNavComponent)
